"use client";

import React from "react";
import { Clock, Building2, User, CalendarClock, Timer } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import type { Ticket } from "@/lib/types/database";
import { isActiveTicketStatus } from "@/lib/constants/ticket-statuses";
import type { Ticket_Status } from "@/src/gql/graphql";

// ============================================================================
// TICKET GLANCE CARD
// ============================================================================
// Compact, read-only "at a glance" panel for the ticket detail sidebar. Surfaces
// the high-level facts a user needs to triage — status/priority, client,
// SLA/due pressure, and time totals — above the editable field panel.
// ============================================================================

interface TicketGlanceCardProps {
  ticket: Ticket;
  className?: string;
}

const STATUS_STYLES: Record<string, string> = {
  open: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200",
  in_progress: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200",
  pending: "bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200",
  resolved: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200",
  closed: "bg-gray-200 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
};

const PRIORITY_STYLES: Record<string, string> = {
  low: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
  medium: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200",
  high: "bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200",
  urgent: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200",
  critical: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200",
};

const titleCase = (value: string | null | undefined): string =>
  value ? value.replace(/_/g, " ") : "—";

const formatDate = (value: string | null | undefined): string => {
  if (!value) return "—";
  const ts = Date.parse(value);
  return Number.isFinite(ts) ? new Date(ts).toLocaleDateString() : "—";
};

const hours = (value: number | null | undefined): string =>
  value != null ? `${value}h` : "—";

function Fact({
  icon: Icon,
  label,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-sm text-foreground break-words">{children}</p>
      </div>
    </div>
  );
}

export function TicketGlanceCard({ ticket, className }: TicketGlanceCardProps) {
  if (!ticket) return null;

  const status = (ticket.status as string | null) ?? null;
  const priority = (ticket.priority as string | null) ?? null;
  // Only an ACTIVE ticket can be overdue. The old `!== "resolved" && !== "closed"`
  // check still flagged `cancelled` (and any future terminal status) as overdue.
  const dueOverdue =
    ticket.due_date != null &&
    Date.parse(ticket.due_date) < Date.now() &&
    status != null &&
    isActiveTicketStatus(status as Ticket_Status);

  return (
    <div
      className={cn(
        "rounded-lg border border-blue-100 dark:border-blue-900/40 bg-card p-4 space-y-4",
        className
      )}
    >
      {/* Status / priority strip */}
      <div className="flex flex-wrap items-center gap-2">
        {ticket.ticket_number != null && (
          <span className="text-xs font-mono text-muted-foreground">#{ticket.ticket_number}</span>
        )}
        {status && (
          <Badge
            variant="secondary"
            className={cn("capitalize", STATUS_STYLES[status] ?? "")}
          >
            {titleCase(status)}
          </Badge>
        )}
        {priority && (
          <Badge
            variant="secondary"
            className={cn("capitalize", PRIORITY_STYLES[priority] ?? "")}
          >
            {titleCase(priority)} priority
          </Badge>
        )}
        {ticket.category && (
          <Badge variant="outline" className="capitalize">
            {titleCase(ticket.category)}
          </Badge>
        )}
      </div>

      <div className="grid grid-cols-1 gap-3">
        {(ticket.company_name || ticket.company_id) && (
          <Fact icon={Building2} label="Company">
            {ticket.company_name ?? "Linked company"}
          </Fact>
        )}
        {(ticket.contact_name || ticket.contact_id) && (
          <Fact icon={User} label="Requester">
            {ticket.contact_name ?? "Linked contact"}
          </Fact>
        )}
        {(ticket.due_date || ticket.sla_hours != null) && (
          <Fact icon={CalendarClock} label="Due / SLA">
            <span className={cn(dueOverdue && "text-red-600 dark:text-red-400 font-medium")}>
              {formatDate(ticket.due_date)}
              {dueOverdue ? " (overdue)" : ""}
            </span>
            {ticket.sla_hours != null && (
              <span className="text-muted-foreground"> · SLA {ticket.sla_hours}h</span>
            )}
          </Fact>
        )}
        <Fact icon={Timer} label="Time">
          <span>est {hours(ticket.estimated_hours)}</span>
          <span className="text-muted-foreground"> · </span>
          <span>actual {hours(ticket.actual_hours ?? 0)}</span>
          <span className="text-muted-foreground"> · </span>
          <span>billable {hours(ticket.billable_hours ?? 0)}</span>
        </Fact>
        <Fact icon={Clock} label="Opened / updated">
          {formatDate(ticket.created_at)} · upd {formatDate(ticket.updated_at)}
        </Fact>
      </div>
    </div>
  );
}
